import { avatarTint, initialsOf } from '@/components/hearth'
import { getRoleDisplayName } from '@/hooks'
import { cn } from '@/lib/utils'
import type { FamilyMember } from '@/types'

interface FamilyMemberCardProps {
  members: FamilyMember[]
  isLoading?: boolean
  /** Shown when the members list is empty */
  emptyMessage?: string
}

function MemberRow({ member }: { member: FamilyMember }) {
  const tint = avatarTint(member.user.full_name)

  return (
    <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
          tint.bg,
          tint.fg
        )}
      >
        {initialsOf(member.user.full_name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-medium text-gray-900">{member.user.full_name}</p>
        <p className="text-sm text-gray-500">{getRoleDisplayName(member.role)}</p>
      </div>
    </div>
  )
}

function MemberRowSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 py-3 first:pt-0 last:pb-0">
      <div className="h-10 w-10 rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 w-24 rounded bg-gray-200" />
        <div className="mt-1.5 h-3 w-16 rounded bg-gray-200" />
      </div>
    </div>
  )
}

/**
 * A calm hearth-card list of family members (avatar + name + role).
 * Used on the parent home under "Looking out for you".
 */
export function FamilyMemberCard({
  members,
  isLoading,
  emptyMessage = 'No family members yet',
}: FamilyMemberCardProps) {
  return (
    <div className="hearth-card p-4">
      {isLoading ? (
        <div className="divide-y divide-gray-100">
          {Array.from({ length: 2 }).map((_, i) => (
            <MemberRowSkeleton key={i} />
          ))}
        </div>
      ) : members.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {members.map((member) => (
            <MemberRow key={member.id} member={member} />
          ))}
        </div>
      ) : (
        <p className="py-6 text-center text-base text-gray-500">{emptyMessage}</p>
      )}
    </div>
  )
}
